/**
 * DeviceEditModal — DEVICE-DETAIL-MANAGE-1B.
 *
 * Edição administrativa SEGURA de metadados do device a partir da página Detalhes:
 * grupo, tags e localização (nome/endereço/lat/lng). Usa SOMENTE endpoints já
 * existentes e guardados no backend (setDeviceGroup/setDeviceTags/setDeviceLocation);
 * NÃO duplica a segurança — a verdade é o backend.
 *
 * Device protegido (produção/gold/canário): salvar LOCALIZAÇÃO exige confirmação
 * forte (useCriticalConfirm → header X-Confirm-*), igual ao Mapa (MAP-MANAGE-1E).
 * Campos perigosos (device_id/MAC/HMAC/firmware/heartbeat) NÃO são tocados aqui.
 *
 * O botão que abre este modal é gated por CAP.FLEET_WRITE (<Can>), então perfis
 * sem fleet:write (noc/viewer) não o veem.
 */
import React, { useEffect, useMemo, useState } from "react";

import {
  getDeviceTags,
  listGroups,
  listTags,
  setDeviceGroup,
  setDeviceLocation,
  setDeviceTags,
} from "../lib/api.js";
import { useCriticalConfirm } from "./ConfirmCriticalAction.jsx";
import { getProtectionState } from "../lib/protection.js";

function asList(res) {
  if (Array.isArray(res)) return res;
  for (const k of ["items", "groups", "tags", "results"]) {
    if (Array.isArray(res?.[k])) return res[k];
  }
  return [];
}

function friendlyErr(e) {
  const s = e?.status;
  if (s === 401) return "Sessão expirada — faça login novamente.";
  if (s === 403) return "Sem permissão (perfil sem fleet:write) ou confirmação recusada.";
  if (s === 404) return "Recurso não encontrado (device/grupo/tag).";
  if (s === 422) return `Dado inválido: ${e?.message || "verifique os campos."}`;
  return e?.message || "Falha ao salvar.";
}

const INPUT =
  "w-full bg-noc-bg border border-noc-border rounded-md px-2 py-1.5 " +
  "text-[13px] text-noc-text focus:border-noc-accent outline-none";

export default function DeviceEditModal({ deviceId, device, geo, onClose, onSaved }) {
  const confirmCritical = useCriticalConfirm();
  const protectedDev = getProtectionState(device).protected;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [groups, setGroups] = useState([]);
  const [tagCatalog, setTagCatalog] = useState([]);

  const initialGroup = String(geo?.group_id ?? device?.group_id ?? "");
  const [groupId, setGroupId] = useState(initialGroup);
  const [tagIds, setTagIds] = useState([]);
  const [initialTagIds, setInitialTagIds] = useState([]);

  const initialLoc = useMemo(() => ({
    name: geo?.name || geo?.label || "",
    address: geo?.address || "",
    lat: geo?.lat ?? geo?.latitude ?? "",
    lng: geo?.lng ?? geo?.lon ?? geo?.longitude ?? "",
  }), [geo]);
  const [loc, setLoc] = useState(initialLoc);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [g, t, dt] = await Promise.all([
          listGroups().catch(() => []),
          listTags().catch(() => []),
          getDeviceTags(deviceId).catch(() => []),
        ]);
        if (!alive) return;
        setGroups(asList(g));
        setTagCatalog(asList(t));
        const cur = asList(dt)
          .map((x) => String(x.id ?? x.tag_id ?? x))
          .filter((x) => x && x !== "undefined");
        setTagIds(cur);
        setInitialTagIds(cur);
      } catch (e) {
        if (alive) setError(friendlyErr(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [deviceId]);

  function toggleTag(id) {
    const s = String(id);
    setTagIds((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  }

  const groupChanged = groupId !== initialGroup;
  const tagsChanged = useMemo(
    () => [...tagIds].sort().join(",") !== [...initialTagIds].sort().join(","),
    [tagIds, initialTagIds],
  );
  const locChanged = (
    loc.name !== initialLoc.name ||
    loc.address !== initialLoc.address ||
    String(loc.lat) !== String(initialLoc.lat) ||
    String(loc.lng) !== String(initialLoc.lng)
  );
  const anyChanged = groupChanged || tagsChanged || locChanged;

  function locCoordsValid() {
    const hasLat = String(loc.lat).trim() !== "";
    const hasLng = String(loc.lng).trim() !== "";
    if (!hasLat && !hasLng) return true; // só nome/endereço — sem coords
    if (hasLat !== hasLng) return false;  // precisa dos dois
    const la = Number(loc.lat), ln = Number(loc.lng);
    return Number.isFinite(la) && la >= -90 && la <= 90 &&
           Number.isFinite(ln) && ln >= -180 && ln <= 180;
  }

  async function handleSave() {
    if (!anyChanged) { onClose(); return; }
    if (locChanged && !locCoordsValid()) {
      setError("Latitude (-90..90) e longitude (-180..180) devem ser numéricas e ambas preenchidas.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (groupChanged) await setDeviceGroup(deviceId, groupId || null);
      if (tagsChanged) await setDeviceTags(deviceId, tagIds);
      if (locChanged) {
        let confirm;
        if (protectedDev) {
          const secret = await confirmCritical({
            actionLabel: "Salvar localização (device PROTEGIDO)",
            target: { kind: "single", deviceId },
            impact: "Alterar a localização de um device protegido (produção/gold/canário).",
          });
          if (secret == null) { setSaving(false); return; }
          confirm = secret;
        }
        const body = {};
        if (loc.name?.trim()) body.name = loc.name.trim();
        if (loc.address?.trim()) body.address = loc.address.trim();
        if (String(loc.lat).trim() !== "") body.lat = Number(loc.lat);
        if (String(loc.lng).trim() !== "") body.lng = Number(loc.lng);
        await setDeviceLocation(deviceId, body, { confirm });
      }
      onSaved && onSaved();
    } catch (e) {
      setError(friendlyErr(e));
      setSaving(false);
      return;
    }
    setSaving(false);
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={saving ? undefined : onClose}
    >
      <div
        className="bg-noc-panel border border-noc-border rounded-xl w-full max-w-lg
                   max-h-[90vh] overflow-y-auto p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-[15px] font-semibold text-noc-text">Editar informações</h2>
          <span className="font-mono text-[11px] text-noc-faint">{deviceId}</span>
        </div>

        {protectedDev && (
          <div className="text-[12px] bg-noc-warn/10 border border-noc-warn/30
                          text-noc-warn rounded-md px-3 py-2">
            🔒 Device PROTEGIDO (produção/gold/canário). Salvar a localização exige
            confirmação forte. Grupo/tags são metadados administrativos.
          </div>
        )}

        {error && (
          <div className="text-[12px] bg-noc-err/10 border border-noc-err/30
                          text-noc-err rounded-md px-3 py-2">{error}</div>
        )}

        {loading ? (
          <div className="text-[12px] font-mono text-noc-faint">Carregando catálogos…</div>
        ) : (
          <>
            {/* Grupo */}
            <div className="space-y-1">
              <label className="text-[11px] uppercase tracking-wide text-noc-faint">Grupo</label>
              <select className={INPUT} value={groupId}
                onChange={(e) => setGroupId(e.target.value)}>
                <option value="">— sem grupo —</option>
                {groups.map((g) => (
                  <option key={g.id} value={String(g.id)}>{g.name || g.id}</option>
                ))}
              </select>
            </div>

            {/* Tags */}
            <div className="space-y-1">
              <label className="text-[11px] uppercase tracking-wide text-noc-faint">Tags</label>
              {tagCatalog.length === 0 ? (
                <div className="text-[12px] text-noc-faint">(nenhuma tag no catálogo)</div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {tagCatalog.map((t) => {
                    const id = String(t.id);
                    const on = tagIds.includes(id);
                    return (
                      <button key={id} type="button" onClick={() => toggleTag(id)}
                        className={"px-2 py-1 rounded-md text-[12px] border transition-colors " +
                          (on
                            ? "bg-noc-accent/15 border-noc-accent/50 text-noc-accent"
                            : "bg-noc-bg border-noc-border text-noc-muted hover:border-noc-accent/40")}>
                        {on ? "✓ " : ""}{t.name || t.id}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Localização */}
            <div className="space-y-1">
              <label className="text-[11px] uppercase tracking-wide text-noc-faint">Localização</label>
              <input className={INPUT} placeholder="Nome (ex.: TORRE-CENTRO)"
                value={loc.name} onChange={(e) => setLoc({ ...loc, name: e.target.value })} />
              <input className={INPUT} placeholder="Endereço"
                value={loc.address} onChange={(e) => setLoc({ ...loc, address: e.target.value })} />
              <div className="flex gap-2">
                <input className={INPUT} placeholder="Latitude (-90..90)" inputMode="decimal"
                  value={loc.lat} onChange={(e) => setLoc({ ...loc, lat: e.target.value })} />
                <input className={INPUT} placeholder="Longitude (-180..180)" inputMode="decimal"
                  value={loc.lng} onChange={(e) => setLoc({ ...loc, lng: e.target.value })} />
              </div>
            </div>

            {/* Read-only — campos perigosos NÃO editáveis aqui */}
            <div className="text-[11px] text-noc-faint border-t border-noc-divider pt-2">
              Somente leitura (não editável): device_id, MAC, HMAC/segredo, firmware
              reportado, último heartbeat, telemetria. Watchdog/WiFi e remover vínculo
              chegam em fases seguintes.
            </div>
          </>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button className="btn-ghost" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving || loading || !anyChanged}>
            {saving ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}
